<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import store from "@/store";
import trim from "lodash-es/trim";

import { toast } from "@/common/utils/toast";
import { getDate, getHour } from "@/common/utils/dates";

import {
  consoleAccessoryConsoleList,
  consoleAccessoryListConsoleIdByAccessory,
  consoleAccessoryCreate,
  consoleAccessoryDelete,
} from "@/common/api/rest/console/accessory";

import { consoleList } from "@/common/api/rest/console/console";

import type { ConsoleAccessoryType } from "@/common/types/console/accessory";
import type { ConsoleType } from "@/common/types/console/console";

import {
  baseAvatar,
  baseConfirm,
  baseLoading,
  baseInput,
  baseModal,
  baseSearchExact,
  baseTable,
  svgIcon,
} from "@/common/components";

interface ConsoleIdType {
  item_id: string;
  console_id: string;
  game_id: string;
}

const { t, locale } = useI18n();

const props = defineProps<{
  ownerId: string;
}>();

const imageSize = "15rem";

const consoles = ref<ConsoleAccessoryType[]>([]);
const consoleIds = ref<ConsoleIdType[]>([]);
const selected = ref<ConsoleAccessoryType | null>(null);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const loading = ref(true);
const showModalAddConsole = ref(false);
const showModalRemoveConsole = ref(false);

const consoleSearchListRef = ref<any>(null);
const consoleSearch = ref<string>("");
const consoleExactSearch = ref<boolean>(false);
const consoleSearchList = ref<ConsoleType[]>([]);
const consoleLimit = ref<number>(10);
const consolePage = ref<number>(0);
const consolePages = ref<number>(0);
const consoleSearched = ref<number>(0);
const consoleTotal = ref<number>(0);
const loadingMoreConsoles = ref(false);

const tableHeader = computed(() => [
  { key: "avatar", label: "" },
  { key: "name", label: t("common.labels.name") },
  { key: "created", label: t("common.labels.created") },
]);

async function fetchConsoles() {
  await consoleAccessoryConsoleList(props.ownerId, {
    page: page.value,
    limit: limit.value,
    search: search.value,
    sort: "DESC",
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        consoles.value = data.list;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function fetchConsoleId() {
  await consoleAccessoryListConsoleIdByAccessory(props.ownerId)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        consoleIds.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function searchConsoles(append = false) {
  if (
    append &&
    (loadingMoreConsoles.value || consolePage.value + 1 >= consolePages.value)
  ) {
    return;
  }

  if (append) {
    loadingMoreConsoles.value = true;
  }

  const pageToFetch = append ? consolePage.value + 1 : 0;

  await consoleList({
    page: pageToFetch,
    limit: consoleLimit.value,
    search: consoleSearch.value,
    match: consoleExactSearch.value ? "exact" : "like",
    sort: "ASC",
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        const newItems = data.list ?? [];
        consoleSearchList.value = append
          ? [...consoleSearchList.value, ...newItems]
          : newItems;
        consolePage.value = pageToFetch;
        consolePages.value = data.infos.pages;
        consoleTotal.value = data.infos.total;
        consoleSearched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      loadingMoreConsoles.value = false;
    });
}

function changePage(value: number) {
  page.value = value;
  fetchConsoles();
}

function searchItem(value: string) {
  search.value = value;

  fetchConsoles();
}

function resetSearchConsole() {
  consoleSearch.value = "";
  consoleExactSearch.value = false;
  consoleSearchList.value = [];
  consolePage.value = 0;
  consolePages.value = 0;
  consoleSearched.value = 0;
  consoleTotal.value = 0;
  loadingMoreConsoles.value = false;
}

async function addConsole() {
  store.commit("SET_LOADING", true);

  resetSearchConsole();
  await searchConsoles();
  store.commit("SET_LOADING", false);

  showModalAddConsole.value = true;
}

async function searchConsole() {
  consoleSearchList.value = [];
  consolePage.value = 0;
  consolePages.value = 0;
  consoleSearched.value = 0;
  consoleTotal.value = 0;

  await searchConsoles();
}

function onConsoleListScroll(event: Event) {
  const target =
    (event.target as HTMLElement | null) ?? consoleSearchListRef.value;

  if (!target) {
    return;
  }

  const reachedEnd =
    target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

  if (
    reachedEnd &&
    !loadingMoreConsoles.value &&
    consolePage.value + 1 < consolePages.value
  ) {
    void searchConsoles(true);
  }
}

async function selectConsole(console: ConsoleType) {
  if (!consoleIds.value.some((c) => c.console_id === console.console_id)) {
    await consoleAccessoryCreate(console.console_id, {
      accessory_id: props.ownerId,
    })
      .then((response) => {
        const { success, message } = response;

        if (success) {
          fetchConsoleId();
          fetchConsoles();
        } else {
          toast({ message: message, type: "error" });
        }
      })
      .catch(() => {
        toast({ message: t("error.default"), type: "error" });
      });
  } else {
    const item = consoleIds.value.find(
      (c) => c.console_id === console.console_id,
    );

    await consoleAccessoryDelete(item.item_id)
      .then((response) => {
        const { success, message } = response;

        if (success) {
          fetchConsoleId();
          fetchConsoles();
        } else {
          toast({ message: message, type: "error" });
        }
      })
      .catch(() => {
        toast({ message: t("error.default"), type: "error" });
      });
  }
}

function removeConsole(accessory: ConsoleAccessoryType) {
  selected.value = accessory;
  showModalRemoveConsole.value = true;
}

function removeConsoleCancel() {
  selected.value = null;
  showModalRemoveConsole.value = false;
}

async function removeConsoleConfirm() {
  if (!selected.value) {
    return;
  }

  await consoleAccessoryDelete(selected.value.item_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchConsoleId();
        fetchConsoles();
        showModalRemoveConsole.value = false;
        selected.value = null;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

watch(
  () => consoleSearch.value,
  () => {
    if (trim(consoleSearch.value).length === 0) {
      searchConsole();
    }
  },
);

watch(
  () => consoleExactSearch.value,
  () => {
    if (trim(consoleSearch.value).length > 0) {
      searchConsole();
    }
  },
);

onMounted(async () => {
  await fetchConsoles();
  await fetchConsoleId();
  loading.value = false;
});
</script>

<template>
  <section class="tab-console">
    <div class="tab-header">
      <svg-icon icon="no-image" size="2rem" />
      {{ t("collection.labels.consoles") }}
    </div>

    <div class="tab-content">
      <template v-if="loading">
        <div class="empty-data">
          <base-loading />
        </div>
      </template>
      <template v-else>
        <base-table
          :header="tableHeader"
          :data="consoles"
          :limit="limit"
          :page="page"
          :pages="pages"
          :total="total"
          :searched="searched"
          :options="true"
          :copy="false"
          :edit="false"
          :del="true"
          @change:page="changePage($event)"
          @searching="searchItem($event)"
          @btn:add="addConsole"
          @btn:del="removeConsole($event)"
        >
          <template #avatar="{ data }">
            <base-avatar
              position="center"
              :is-edit="false"
              :show-image="true"
              :url="data?.avatar?.large"
              :aspect-ratio="1.333"
              size="3rem"
              border-radius="0.25rem"
            />
          </template>

          <template #created="{ data }">
            <span>
              {{ getDate(data.created) }} - {{ getHour(data.created) }}
            </span>
          </template>
        </base-table>
      </template>
    </div>
  </section>

  <base-confirm
    :show="showModalRemoveConsole"
    :title="t('collection.modals.titles.del_console').toUpperCase()"
    :message="
      t('collection.modals.messages.del_console', {
        name: selected?.name?.toUpperCase(),
      })
    "
    @cancel="removeConsoleCancel()"
    @confirm="removeConsoleConfirm()"
  />

  <base-modal
    :show="showModalAddConsole"
    :title="t('collection.modals.titles.add_console').toUpperCase()"
    @close="showModalAddConsole = false"
  >
    <template #content>
      <div class="game-add">
        <div class="game-search">
          <base-input
            v-model="consoleSearch"
            background="#fff"
            color="#000"
            :placeholder="t('common.labels.search')"
            @key:enter="searchConsole()"
          >
            <template #sufix>
              <div class="icon">
                <svg-icon
                  icon="search"
                  size="1.75rem"
                  @click="searchConsole()"
                />
              </div>
            </template>
          </base-input>
        </div>

        <base-search-exact v-model="consoleExactSearch" />

        <div
          ref="consoleSearchListRef"
          class="game-list"
          @scroll="onConsoleListScroll"
        >
          <template v-for="(item, index) in consoleSearchList" :key="index">
            <div class="game">
              <base-avatar
                position="center"
                :is-edit="false"
                :show-image="true"
                :url="item?.avatar?.large"
                :aspect-ratio="1.333"
                size="3rem"
                border-radius="0.25rem"
              ></base-avatar>

              <div class="name">
                {{ item.name }}
              </div>
              <label
                class="custom-checkbox"
                :for="`console-${item.console_id}`"
              >
                <input
                  :id="`console-${item.console_id}`"
                  class="checkbox-input"
                  type="checkbox"
                  @change="selectConsole(item)"
                  :checked="
                    consoleIds.some((c) => c.console_id === item.console_id)
                  "
                />
                <span class="checkbox-mark"></span>
              </label>
            </div>
          </template>
        </div>
      </div>
    </template>
  </base-modal>
</template>

<style lang="scss" scoped>
.tab-console {
  display: flex;
  flex-direction: column;
  padding-block: 1rem;
  gap: 1rem;

  .tab-header {
    display: inline-flex;
    gap: 0.5rem;
    font-size: 1.2rem;
    align-items: center;
    padding-block-end: 0.75rem;
    text-transform: uppercase;
  }

  .tab-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .images {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 1rem;
      padding: 1rem;
      background: var(--white-off);
      border-radius: 0.5rem;
    }

    .list {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-start;
      align-items: flex-start;
      width: 100%;
      gap: 1.5rem;
      row-gap: 1.5rem;
      padding: 1rem;
      background: var(--white-off);
      border-radius: 0.25rem;

      .image {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        background: white;
        border-radius: 0.5rem;
        padding: 1rem;
        margin-block-end: 1.5rem;

        .avatar {
          display: flex;
          align-items: center;
          justify-content: center;
          width: v-bind("imageSize");
          height: auto;
          min-width: 3rem;
          min-height: 3rem;
          color: var(--text-medium);
          overflow: hidden;
        }
      }
    }

    .actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }

    .empty-data {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding-block: 2rem;
      background: var(--white-off);
      border-radius: 0.5rem;
    }
  }
}

.game-add {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .game-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    overflow-y: scroll;
    max-height: 20rem;
    height: 20rem;
    padding: 0.5rem;
    background: white;
    border-radius: 0.5rem;

    .game {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      gap: 0.5rem;
      padding-block: 0.5rem;
      padding-inline: 1rem;
      background: #f1f1f1;
      border-radius: 0.5rem;

      .custom-checkbox {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-inline-start: auto;
        cursor: pointer;
      }

      .checkbox-input {
        position: absolute;
        opacity: 0;
        pointer-events: none;
      }

      .checkbox-mark {
        width: 1.1rem;
        height: 1.1rem;
        border: 0.125rem solid var(--primary);
        border-radius: 0.25rem;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        background: white;
      }

      .checkbox-input:checked + .checkbox-mark {
        background: var(--primary);
        border-color: var(--primary);
      }

      .checkbox-input:checked + .checkbox-mark::after {
        content: "";
        width: 0.3rem;
        height: 0.6rem;
        border: solid white;
        border-width: 0 0.125rem 0.125rem 0;
        transform: rotate(45deg);
        margin-top: -0.1rem;
      }
    }
  }
}

@media screen and (max-width: 1024px) {
  .tab-console {
    .tab-content {
      padding-inline: 0;
    }
  }
}
</style>
